import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiTrash2, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import { useConversationStore } from '../../stores/conversationStore';
import { useAuth } from '../../contexts/AuthContext';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: string;
  sequenceId: number;
  metadata?: any;
  isOptimistic?: boolean;
  replyToMessageId?: string;
  isDeleted?: boolean;
  readReceipts?: { [key: string]: string };
}

interface MessageThreadProps {
  message: Message;
  allMessages: Message[];
  onReply: (messageId: string) => void;
  conversationId: string;
  level?: number;
}

const MessageThread: React.FC<MessageThreadProps> = ({ message, allMessages, onReply, conversationId, level = 0 }) => {
  const replies = allMessages.filter(m => m.replyToMessageId === message.id);
  const { deleteMessage, editMessage, socket } = useConversationStore();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(message.content);
  const messageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Don't track read receipts if no user or socket
    if (!user?.id || !socket) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !message.readReceipts?.[user.id]) {
          socket.emit('messageRead', {
            conversationId,
            messageId: message.id,
            userId: user.id
          });
          observer.disconnect();
        }
      },
      { threshold: 1.0 }
    );

    const element = messageRef.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
    };
  }, [conversationId, message.id, message.readReceipts, socket, user?.id]);

  const handleDelete = () => {
    deleteMessage(conversationId, message.id);
  };

  const handleSaveEdit = () => {
    if (editedContent.trim() !== message.content) {
      editMessage(conversationId, message.id, editedContent);
    }
    setIsEditing(false);
  };

  return (
    <div ref={messageRef} style={{ marginLeft: `${level * 20}px` }} className="mb-4 group">
      {/* Render the message here */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start gap-2"
      >
        <div className="flex-1">
          {message.isDeleted ? (
            <p className="italic text-gray-500">{message.content}</p>
          ) : isEditing ? (
            <div>
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full p-2 border rounded"
              />
              <div className="flex gap-2 mt-1">
                <button onClick={handleSaveEdit} className="text-green-500"><FiCheck /></button>
                <button onClick={() => setIsEditing(false)} className="text-red-500"><FiX /></button>
              </div>
            </div>
          ) : (
            <p>{message.content}</p>
          )}
          {!message.isDeleted && !isEditing && (
            <div className="flex items-center gap-2">
              <button onClick={() => onReply(message.id)} className="text-xs text-blue-500">Reply</button>
              {message.readReceipts && (
                <span className="text-xs text-gray-400">
                  Read by {Object.keys(message.readReceipts).length}
                </span>
              )}
            </div>
          )}
        </div>
        {!message.isDeleted && !isEditing && (
          <div className="opacity-0 group-hover:opacity-100 flex gap-2">
            <button onClick={() => setIsEditing(true)} className="text-gray-400 hover:text-blue-500">
              <FiEdit2 size={14} />
            </button>
            <button onClick={handleDelete} className="text-gray-400 hover:text-red-500">
              <FiTrash2 size={14} />
            </button>
          </div>
        )}
      </motion.div>

      {replies.length > 0 && level < 3 && (
        <div className="mt-2 pl-4 border-l-2 border-gray-300">
          {replies.map(reply => (
            <MessageThread
              key={reply.id}
              message={reply}
              allMessages={allMessages}
              onReply={onReply}
              conversationId={conversationId}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageThread;
